export type OvertimeSettings = {
  dailyTargetHours: number;
  overtimeMultiplier: number;
  overtimeRules: OvertimeRule[];
};

export type OvertimeRule = {
  startDate: string;
  endDate: string | null;
  dailyTargetHours: number;
  overtimeMultiplier: number;
};

export type OvertimeCostSegment = {
  bucketKey: string;
  userId?: number | null;
  activityId?: number | null;
  at: Date | null;
  hours: number;
  rate: number | null;
};

export type OvertimeAllocationResult = {
  costByActivityId: Map<number, number>;
  costByUserId: Map<number, number>;
  totalCost: number;
};

type EffectiveOvertimeSettings = {
  dailyTargetHours: number;
  overtimeMultiplier: number;
};

const toDateKey = (value: Date | null): string | null => {
  if (!value || !Number.isFinite(value.getTime())) {
    return null;
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getOvertimeSettings = (
  companySettings: Record<string, any> | null | undefined,
): OvertimeSettings => {
  const dailyTargetHours = Number(companySettings?.workHours?.dailyTargetHours);
  const overtimeMultiplier = Number(
    companySettings?.workHours?.overtimeMultiplier,
  );

  const rawRules = companySettings?.workHours?.overtimeRules;
  const overtimeRules = Array.isArray(rawRules)
    ? rawRules
        .map((rule): OvertimeRule | null => {
          const startDate =
            typeof rule?.startDate === "string" ? rule.startDate.trim() : "";
          if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
            return null;
          }

          const endDateRaw =
            typeof rule?.endDate === "string" ? rule.endDate.trim() : "";
          const endDate =
            endDateRaw && /^\d{4}-\d{2}-\d{2}$/.test(endDateRaw)
              ? endDateRaw
              : null;
          if (endDate && endDate < startDate) {
            return null;
          }

          const ruleDailyTargetHours = Number(rule?.dailyTargetHours);
          const ruleOvertimeMultiplier = Number(rule?.overtimeMultiplier);
          if (
            !Number.isFinite(ruleDailyTargetHours) ||
            ruleDailyTargetHours <= 0 ||
            !Number.isFinite(ruleOvertimeMultiplier) ||
            ruleOvertimeMultiplier <= 0
          ) {
            return null;
          }

          return {
            startDate,
            endDate,
            dailyTargetHours: ruleDailyTargetHours,
            overtimeMultiplier: ruleOvertimeMultiplier,
          };
        })
        .filter((rule): rule is OvertimeRule => rule !== null)
        .sort((left, right) => left.startDate.localeCompare(right.startDate))
    : [];

  return {
    dailyTargetHours:
      Number.isFinite(dailyTargetHours) && dailyTargetHours > 0
        ? dailyTargetHours
        : 9,
    overtimeMultiplier:
      Number.isFinite(overtimeMultiplier) && overtimeMultiplier > 0
        ? overtimeMultiplier
        : 1.5,
    overtimeRules,
  };
};

export const resolveOvertimeSettingsForDate = (
  settings: OvertimeSettings,
  at: Date | null,
): EffectiveOvertimeSettings => {
  const fallbackDailyTargetHours =
    Number.isFinite(settings.dailyTargetHours) && settings.dailyTargetHours > 0
      ? settings.dailyTargetHours
      : 9;
  const fallbackOvertimeMultiplier =
    Number.isFinite(settings.overtimeMultiplier) && settings.overtimeMultiplier > 0
      ? settings.overtimeMultiplier
      : 1.5;
  const dateKey = toDateKey(at);
  if (!dateKey || !Array.isArray(settings.overtimeRules) || !settings.overtimeRules.length) {
    return {
      dailyTargetHours: fallbackDailyTargetHours,
      overtimeMultiplier: fallbackOvertimeMultiplier,
    };
  }

  for (let index = settings.overtimeRules.length - 1; index >= 0; index -= 1) {
    const rule = settings.overtimeRules[index];
    if (
      rule.startDate <= dateKey &&
      (!rule.endDate || dateKey <= rule.endDate)
    ) {
      return {
        dailyTargetHours: rule.dailyTargetHours,
        overtimeMultiplier: rule.overtimeMultiplier,
      };
    }
  }

  return {
    dailyTargetHours: fallbackDailyTargetHours,
    overtimeMultiplier: fallbackOvertimeMultiplier,
  };
};

export const allocateOvertimeCosts = ({
  segments,
  dailyTargetHours,
  overtimeMultiplier,
  overtimeSettings,
}: {
  segments: OvertimeCostSegment[];
  dailyTargetHours?: number;
  overtimeMultiplier?: number;
  overtimeSettings?: OvertimeSettings;
}): OvertimeAllocationResult => {
  const costByActivityId = new Map<number, number>();
  const costByUserId = new Map<number, number>();

  if (!segments.length) {
    return {
      costByActivityId,
      costByUserId,
      totalCost: 0,
    };
  }

  const groups = new Map<string, OvertimeCostSegment[]>();
  segments.forEach((segment, index) => {
    if (!Number.isFinite(segment.hours) || segment.hours <= 0) {
      return;
    }

    const dateKey = toDateKey(segment.at);
    const groupKey = `${segment.bucketKey}::${dateKey ?? `unknown-${index}`}`;
    const existing = groups.get(groupKey) || [];
    existing.push(segment);
    groups.set(groupKey, existing);
  });

  let totalCost = 0;
  const effectiveOvertimeSettings =
    overtimeSettings ??
    ({
      dailyTargetHours:
        typeof dailyTargetHours === "number" &&
        Number.isFinite(dailyTargetHours) &&
        dailyTargetHours > 0
          ? dailyTargetHours
          : 9,
      overtimeMultiplier:
        typeof overtimeMultiplier === "number" &&
        Number.isFinite(overtimeMultiplier) &&
        overtimeMultiplier > 0
          ? overtimeMultiplier
          : 1.5,
      overtimeRules: [],
    } satisfies OvertimeSettings);

  groups.forEach((groupSegments) => {
    const sortedSegments = [...groupSegments].sort((left, right) => {
      const leftTime = left.at?.getTime() ?? 0;
      const rightTime = right.at?.getTime() ?? 0;
      return leftTime - rightTime;
    });
    const resolvedSettings = resolveOvertimeSettingsForDate(
      effectiveOvertimeSettings,
      sortedSegments[0]?.at ?? null,
    );

    let consumedHours = 0;
    sortedSegments.forEach((segment) => {
      const normalHours = Math.max(
        0,
        Math.min(segment.hours, resolvedSettings.dailyTargetHours - consumedHours),
      );
      const overtimeHours = Math.max(0, segment.hours - normalHours);
      const rate =
        typeof segment.rate === "number" && Number.isFinite(segment.rate)
          ? segment.rate
          : null;
      const segmentCost =
        rate === null
          ? 0
          : normalHours * rate + overtimeHours * rate * resolvedSettings.overtimeMultiplier;

      consumedHours += segment.hours;
      totalCost += segmentCost;

      if (
        typeof segment.activityId === "number" &&
        Number.isFinite(segment.activityId)
      ) {
        costByActivityId.set(
          segment.activityId,
          (costByActivityId.get(segment.activityId) ?? 0) + segmentCost,
        );
      }

      if (typeof segment.userId === "number" && Number.isFinite(segment.userId)) {
        costByUserId.set(
          segment.userId,
          (costByUserId.get(segment.userId) ?? 0) + segmentCost,
        );
      }
    });
  });

  return {
    costByActivityId,
    costByUserId,
    totalCost,
  };
};
